<script setup lang="ts">
import { reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, email as emailRule, requiredIf, numeric, minLength, maxLength } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import { useAuthStore } from '@/stores/useAuthStore'
import FileUpload from '../../Shared/FileUpload.vue'
import SSNInput from '../../Shared/SSNInput.vue'

const { t } = useI18n()
const { umsokn, onLoaded } = useUmsokn()
const authStore = useAuthStore()

const props = defineProps<{
  disable?: boolean
  subHeader?: string
}>()

// track whether spouse-disability doc is provided
const fileValidation = reactive({
  filerequired: false
})

const rules = {
  umsokn: {
    name: { required },
    ssn: { required },
    address: { required },
    postalCode: { required },
    email: { required, email: emailRule },
    phone: { required, numeric, minLength: minLength(7), maxLength: maxLength(15) },
    makiOryrki: { required }
  },
  fileValidation: {
    filerequired: {
      required: () =>
        umsokn.value?.makiOryrki !== 1 || fileValidation.filerequired === true
    }
  }
}

const v$ = useVuelidate(rules, { umsokn, fileValidation })

onLoaded(async () => {
  try {
    const user = await authStore.getAuthUser()
    if (user && umsokn.value) {
      umsokn.value.name = user.name
      umsokn.value.ssn = user.username
    }
  } catch (e) {
    console.error('Failed to prefill auth user', e)
  }
})

function digitsOnly(val: string) {
  return (val || '').replace(/\D/g, '')
}

defineExpose({ validate })

function validate() {
  v$.value.$touch()
  return !v$.value.$invalid
}
</script>

<template>
  <v-row v-if="umsokn">
    <v-col cols="12">
      <h2 class="form__header">
        {{ t('umsokn_umsaekjandi_header') }}
      </h2>
    </v-col>

    <v-col v-if="props.subHeader" cols="12">
      <p>{{ props.subHeader }}</p>
    </v-col>

    <!-- Name (disabled) -->
    <v-col cols="12" md="6">
      <v-text-field
        v-model="umsokn.name"
        :label="t('umsokn_umsaekjandi_nafn')"
        variant="outlined"
        readonly
        :error="v$.umsokn.name.$error"
        :error-messages="v$.umsokn.name.$error ? [t('umsokn_umsaekjandi_nafn_error')] : []"
      />
    </v-col>

    <!-- SSN (disabled) -->
    <v-col cols="12" md="6">
      <SSNInput
        v-model="umsokn.ssn"
        :label="t('umsokn_umsaekjandi_kennitala')"
        variant="outlined"
        readonly
        :error="v$.umsokn.ssn.$error"
        :error-messages="v$.umsokn.ssn.$error ? [t('umsokn_umsaekjandi_kennitala_error')] : []"
      />
    </v-col>

    <!-- Address -->
    <v-col cols="12" md="6">
      <v-text-field
        v-model="umsokn.address"
        :label="t('umsokn_umsaekjandi_heimilisfang')"
        variant="outlined"
        :readonly="props.disable"
        :error="v$.umsokn.address.$error"
        :error-messages="v$.umsokn.address.$error ? [t('umsokn_umsaekjandi_heimilisfang_error')] : []"
      />
    </v-col>

    <!-- Postal Code -->
    <v-col cols="12" md="6">
      <v-text-field
        v-model="umsokn.postalCode"
        :label="t('umsokn_umsaekjandi_postnumer')"
        variant="outlined"
        :readonly="props.disable"
        :error="v$.umsokn.postalCode.$error"
        :error-messages="v$.umsokn.postalCode.$error ? [t('umsokn_umsaekjandi_postnumer_error')] : []"
      />
    </v-col>

    <!-- Email -->
    <v-col cols="12" md="6">
      <v-text-field
        v-model="umsokn.email"
        :label="t('umsokn_umsaekjandi_netfang')"
        variant="outlined"
        :readonly="props.disable"
        type="email"
        :error="v$.umsokn.email.$error"
        :error-messages="v$.umsokn.email.$error
          ? [
              !v$.umsokn.email.required ? t('umsokn_umsaekjandi_netfang_error') : t('umsokn_umsaekjandi_netfang_invalid')
            ]
          : []"
      />
    </v-col>

    <!-- Phone -->
    <v-col cols="12" md="6">
      <v-text-field
        v-model="umsokn.phone"
        :label="t('umsokn_umsaekjandi_simanumer')"
        variant="outlined"
        :readonly="props.disable"
        type="tel"
        inputmode="numeric"
        @update:model-value="v => (umsokn.phone = digitsOnly(v))"
        :error="v$.umsokn.phone.$error"
        :error-messages="v$.umsokn.phone.$error ? [t('umsokn_umsaekjandi_simanumer_error')] : []"
      />
    </v-col>

    <!-- Spouse disability radio -->
    <v-col cols="12">
      <p>{{ t('makalifeyrir_umsokn_step1_makioryrki') }}</p>
      <v-radio-group
        v-model="umsokn.makiOryrki"
        inline
        hide-details
        :readonly="props.disable"
        :error="v$.umsokn.makiOryrki.$error"
        :error-messages="v$.umsokn.makiOryrki.$error ? [t('makalifeyrir_umsokn_step1_makioryrki_error')] : []"
      >
        <v-radio :label="t('makalifeyrir_umsokn_step1_makioryrki_yes')" :value="1" />
        <v-radio :label="t('makalifeyrir_umsokn_step1_makioryrki_no')" :value="2" />
      </v-radio-group>
    </v-col>

    <!-- Conditional file upload when makiOryrki === 1 -->
    <v-col v-if="umsokn.makiOryrki === 1" cols="12">
      <p style="font-weight: bold">{{ t('makalifeyrir_umsokn_makioryrki_message') }}</p>
      <div class="mb-2">
        <label for="makalifeyrir_umsokn_step1_makioryrki_file">
          {{ t('makalifeyrir_umsokn_step1_makioryrki_file') }}
        </label>
      </div>

      <!-- Keep your existing upload component/contracts -->
       <FileUpload
          :umsoknType="13"
          :umsoknId="umsokn.id"
          :filespace="0"
          :labelText="$t('makalifeyrir_umsokn_step1_makioryrki_file')"
          :disable="disable"
          @uploaded="fileValidation.filerequired = true"
          @removed="fileValidation.filerequired = false"
        />

      <div class="error__text" style="color: rgb(var(--v-theme-error));" v-if="v$.fileValidation.filerequired.$error">
        {{ t('makalifeyrir_umsokn_step1_makioryrki_fileupload_error') }}
      </div>
    </v-col>

    <v-col cols="12">
      <p style="font-weight: bold">
        {{ t('makalifeyrir_umsokn_step1_athygli') }}
      </p>
    </v-col>
  </v-row>
</template>
